/**
 * In-game feedback via Web3Forms (static-friendly email API).
 */
import './feedback.css';
import { trackEvent } from './analytics';
import { getAppVersion } from './earlyAccess';
import { getLocale, t } from './i18n';
import {
  FEEDBACK_API_URL,
  FEEDBACK_TO_EMAIL,
  getFeedbackAccessKey,
  isFeedbackEnabled,
} from './feedbackConfig';

const USERNAME_STORAGE_KEY = 'explowords-feedback-username';

export type FeedbackContext = {
  gameMode?: string;
  screen?: string;
};

let contextProvider: (() => FeedbackContext) | undefined;
let wired = false;

function formatFeedbackDate(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function getStoredUsername(): string {
  try {
    return localStorage.getItem(USERNAME_STORAGE_KEY)?.trim() ?? '';
  } catch {
    return '';
  }
}

function storeUsername(name: string): void {
  try {
    localStorage.setItem(USERNAME_STORAGE_KEY, name);
  } catch {
    /* ignore */
  }
}

function categoryLabel(value: string): string {
  switch (value) {
    case 'bug':
      return t('feedback.categoryBug');
    case 'suggestion':
      return t('feedback.categorySuggestion');
    case 'praise':
      return t('feedback.categoryPraise');
    default:
      return t('feedback.categoryOther');
  }
}

function setStatus(el: HTMLElement | null, message: string, kind: 'ok' | 'err' | ''): void {
  if (!el) return;
  if (!message) {
    el.textContent = '';
    el.classList.add('hidden');
    el.classList.remove('ok', 'err');
    return;
  }
  el.textContent = message;
  el.classList.remove('hidden', 'ok', 'err');
  el.classList.add(kind === 'ok' ? 'ok' : 'err');
}

function updateCategoryOptions(select: HTMLSelectElement): void {
  const options: { value: string; key: string }[] = [
    { value: 'bug', key: 'feedback.categoryBug' },
    { value: 'suggestion', key: 'feedback.categorySuggestion' },
    { value: 'praise', key: 'feedback.categoryPraise' },
    { value: 'other', key: 'feedback.categoryOther' },
  ];
  const current = select.value || 'suggestion';
  select.innerHTML = '';
  for (const opt of options) {
    const el = document.createElement('option');
    el.value = opt.value;
    el.textContent = t(opt.key);
    select.appendChild(el);
  }
  select.value = options.some((o) => o.value === current) ? current : 'suggestion';
}

function openFeedbackModal(): void {
  const modal = document.getElementById('feedback-modal');
  const form = document.getElementById('feedback-form') as HTMLFormElement | null;
  const unconfigured = document.getElementById('feedback-unconfigured');
  const username = document.getElementById('feedback-username') as HTMLInputElement | null;
  const category = document.getElementById('feedback-category') as HTMLSelectElement | null;
  const status = document.getElementById('feedback-status');
  const submitBtn = document.getElementById('feedback-submit') as HTMLButtonElement | null;

  if (!modal || !form) return;

  trackEvent('feedback_open');

  setStatus(status, '', '');
  if (category) updateCategoryOptions(category);
  if (username && !username.value.trim()) username.value = getStoredUsername();

  const enabled = isFeedbackEnabled();
  form.classList.toggle('hidden', !enabled);
  if (unconfigured) unconfigured.classList.toggle('hidden', enabled);
  if (submitBtn) submitBtn.disabled = !enabled;

  const fab = document.getElementById('feedback-fab');
  if (fab) fab.setAttribute('aria-expanded', 'true');

  modal.classList.remove('hidden');
  (username ?? form.querySelector('input'))?.focus();
}

function closeFeedbackModal(): void {
  document.getElementById('feedback-modal')?.classList.add('hidden');
  const fab = document.getElementById('feedback-fab');
  if (fab) fab.setAttribute('aria-expanded', 'false');
}

async function submitFeedback(e: Event): Promise<void> {
  e.preventDefault();
  const form = e.target as HTMLFormElement;
  const accessKey = getFeedbackAccessKey();
  if (!accessKey) return;

  const usernameEl = document.getElementById('feedback-username') as HTMLInputElement;
  const emailEl = document.getElementById('feedback-email') as HTMLInputElement;
  const categoryEl = document.getElementById('feedback-category') as HTMLSelectElement;
  const messageEl = document.getElementById('feedback-message') as HTMLTextAreaElement;
  const status = document.getElementById('feedback-status');
  const submitBtn = document.getElementById('feedback-submit') as HTMLButtonElement;

  const username = usernameEl?.value.trim() ?? '';
  const email = emailEl?.value.trim() ?? '';
  const category = categoryEl?.value ?? 'other';
  const description = messageEl?.value.trim() ?? '';

  if (!username) {
    setStatus(status, t('feedback.errorUsername'), 'err');
    usernameEl?.focus();
    return;
  }
  if (!description) {
    setStatus(status, t('feedback.errorMessage'), 'err');
    messageEl?.focus();
    return;
  }

  const ctx = contextProvider?.() ?? {};
  const dateStr = formatFeedbackDate();
  const subject = `[Explowords Feedback] ${username} ${dateStr}`;

  const body = [
    `Category: ${categoryLabel(category)}`,
    `Username: ${username}`,
    email ? `Reply email: ${email}` : 'Reply email: (not provided)',
    '',
    '--- Message ---',
    description,
    '',
    '--- Context ---',
    `To: ${FEEDBACK_TO_EMAIL}`,
    `App version: ${getAppVersion()}`,
    `Locale: ${getLocale()}`,
    `Page: ${typeof location !== 'undefined' ? location.pathname : ''}`,
    ctx.screen ? `Screen: ${ctx.screen}` : '',
    ctx.gameMode ? `Game mode: ${ctx.gameMode}` : '',
    `URL: ${typeof location !== 'undefined' ? location.href : ''}`,
    `User-Agent: ${typeof navigator !== 'undefined' ? navigator.userAgent : ''}`,
  ]
    .filter(Boolean)
    .join('\n');

  submitBtn.disabled = true;
  setStatus(status, t('feedback.sending'), '');

  try {
    const res = await fetch(FEEDBACK_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        access_key: accessKey,
        subject,
        from_name: username,
        email: email || 'noreply@explowords.com',
        message: body,
        botcheck: '',
      }),
    });

    const data = (await res.json()) as { success?: boolean; message?: string };
    if (!res.ok || !data.success) {
      throw new Error(data.message || `HTTP ${res.status}`);
    }

    storeUsername(username);
    trackEvent('feedback_submit', { category });
    setStatus(status, t('feedback.success'), 'ok');
    messageEl.value = '';
    window.setTimeout(() => closeFeedbackModal(), 1800);
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    trackEvent('feedback_error', { reason: msg.slice(0, 120) });
    setStatus(status, t('feedback.errorSend'), 'err');
  } finally {
    submitBtn.disabled = false;
  }
}

function detectScreen(): string {
  const panels: [string, string][] = [
    ['game-ui', 'playing'],
    ['game-over-modal', 'game_over'],
    ['pause-modal', 'pause'],
    ['game-mode-menu', 'mode_menu'],
    ['main-menu', 'main_menu'],
    ['previous-days-menu', 'previous_days'],
    ['config-modal', 'config'],
    ['premium-modal', 'premium'],
  ];
  for (const [id, label] of panels) {
    const el = document.getElementById(id);
    if (el && !el.classList.contains('hidden')) return label;
  }
  return 'unknown';
}

/** Wire floating feedback button + modal. Safe to call once per page. */
export function initFeedback(provider?: () => FeedbackContext): void {
  if (wired) return;
  const fab = document.getElementById('feedback-fab');
  const modal = document.getElementById('feedback-modal');
  const form = document.getElementById('feedback-form');
  if (!fab || !modal || !form) return;

  wired = true;
  contextProvider = provider ?? (() => ({ screen: detectScreen() }));

  fab.setAttribute('aria-expanded', 'false');
  fab.addEventListener('click', openFeedbackModal);
  document.getElementById('feedback-cancel')?.addEventListener('click', closeFeedbackModal);
  form.addEventListener('submit', (ev) => void submitFeedback(ev));

  modal.addEventListener('click', (ev) => {
    if (ev.target === modal) closeFeedbackModal();
  });

  document.addEventListener('keydown', (ev) => {
    if (ev.key !== 'Escape') return;
    if (!modal.classList.contains('hidden')) {
      closeFeedbackModal();
      ev.preventDefault();
    }
  });
}
